import { useEffect, useState, useCallback, useMemo } from "react";
import DOMPurify from "dompurify";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard, CalendarDays, Megaphone, Dog as DogIcon, Cat, AlertTriangle,
  Users, FileDown, LogOut, PawPrint, Plus, Trash2, Pin, Upload, Layers, Calendar,
  RefreshCw, KeyRound, Mail, MessageSquare, Pencil,
} from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function severityClass(sev) {
  if (sev === "high") return "bg-destructive";
  if (sev === "low") return "bg-muted-foreground";
  return "bg-terracotta";
}

function issueStatusClass(status) {
  if (status === "resolved") return "bg-fern";
  if (status === "in_progress") return "bg-terracotta";
  return "bg-muted-foreground";
}

async function openProtectedImage(path) {
  try {
    const res = await api.get(`/files/${path}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank", "noopener,noreferrer");
    // Best-effort cleanup
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    console.error("Failed to load photo:", err);
    toast.error("Couldn't load photo");
  }
}

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "shifts", label: "Shifts", icon: CalendarDays },
  { id: "dogs", label: "Dogs", icon: DogIcon },
  { id: "cats", label: "Cats", icon: Cat },
  { id: "bulletins", label: "Bulletins", icon: Megaphone },
  { id: "issues", label: "Issues", icon: AlertTriangle },
  { id: "volunteers", label: "Volunteers", icon: Users },
  { id: "shelter-sync", label: "Shelter sync", icon: RefreshCw },
  { id: "volunteer-sync", label: "Volunteer sync", icon: FileDown },
];

export default function AdminApp() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("dashboard");
  const [shifts, setShifts] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [cats, setCats] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [issues, setIssues] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [showPastShifts, setShowPastShifts] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      // Default: only today + future (local calendar day).
      // When "Show past" is on, drop the date floor and include archived rows.
      let shiftsUrl;
      if (showPastShifts) {
        shiftsUrl = "/shifts?upcoming=false&include_archived=true";
      } else {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        shiftsUrl = `/shifts?from_date=${encodeURIComponent(today.toISOString())}`;
      }
      const [s, d, c, b, i, v] = await Promise.all([
        api.get(shiftsUrl),
        api.get("/dogs?species=dog"),
        api.get("/dogs?species=cat"),
        api.get("/bulletins"),
        api.get("/issues"),
        api.get("/admin/volunteers"),
      ]);
      setShifts(s.data); setDogs(d.data); setCats(c.data); setBulletins(b.data); setIssues(i.data); setVolunteers(v.data);
    } catch (err) {
      console.error("Failed to load admin data:", err);
      toast.error("Couldn't load admin data. Please refresh.");
    }
  }, [showPastShifts]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const totalHours = volunteers.reduce((a, v) => a + (v.total_hours || 0), 0);
  const openIssues = issues.filter((i) => i.status !== "resolved").length;

  return (
    <div className="min-h-screen bg-sand flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 bg-white border-r border-border flex-col">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <PawPrint className="w-6 h-6 text-fern" strokeWidth={1.5} />
            <div>
              <p className="label-caps leading-none">Admin</p>
              <p className="font-heading font-semibold leading-tight">Satchel&apos;s Last Resort</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setView(n.id)} data-testid={`admin-nav-${n.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition
                ${view === n.id ? "bg-fern/10 text-fern" : "text-ink hover:bg-accent/40"}`}>
              <n.icon className="w-4 h-4" strokeWidth={1.5} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <Button variant="ghost" onClick={logout} data-testid="admin-logout" className="w-full justify-start rounded-xl">
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-4 sm:p-8 max-w-6xl">
        {/* Mobile selector */}
        <div className="md:hidden mb-4 flex gap-2 overflow-x-auto pb-2">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setView(n.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs whitespace-nowrap
                ${view === n.id ? "bg-fern text-white" : "bg-white border border-border"}`}>
              <n.icon className="w-3 h-3" strokeWidth={1.5} />{n.label}
            </button>
          ))}
        </div>

        {view === "dashboard" && (
          <div className="space-y-6">
            <div>
              <p className="label-caps">Control room</p>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Welcome, {user?.name?.split(" ")[0]}.</h1>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Volunteers" value={volunteers.length} />
              <Stat label="Logged hours" value={totalHours} />
              <Stat label="Open issues" value={openIssues} />
              <Stat label="Upcoming shifts" value={shifts.filter((s) => new Date(s.starts_at) >= new Date()).length} />
            </div>
            <Card className="tactile-card border-0 p-6">
              <h3 className="font-heading text-lg font-semibold mb-3">Recent issue reports</h3>
              {issues.slice(0, 5).length === 0 ? (
                <p className="text-sm text-muted-foreground">No issues reported.</p>
              ) : (
                <div className="divide-y divide-border">
                  {issues.slice(0, 5).map((i) => (
                    <div key={i.id} className="py-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">{i.title}</p>
                        <p className="text-xs text-muted-foreground">{i.reporter_name} · {i.dog_name || "General"} · {fmt(i.created_at)}</p>
                      </div>
                      <Badge className={`rounded-full ${i.severity === "high" ? "bg-destructive" : "bg-muted-foreground"} text-white`}>{i.severity}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {view === "shifts" && <AdminShifts shifts={shifts} volunteers={volunteers} reload={loadAll} showPast={showPastShifts} setShowPast={setShowPastShifts} />}
        {view === "dogs" && <AdminAnimals animals={dogs} species="dog" reload={loadAll} />}
        {view === "cats" && <AdminAnimals animals={cats} species="cat" reload={loadAll} />}
        {view === "bulletins" && <AdminBulletins bulletins={bulletins} reload={loadAll} />}
        {view === "issues" && <AdminIssues issues={issues} reload={loadAll} />}
        {view === "volunteers" && <AdminVolunteers volunteers={volunteers} reload={loadAll} />}
        {view === "shelter-sync" && <AdminShelterSync reload={loadAll} />}
        {view === "volunteer-sync" && <AdminVolunteerSync reload={loadAll} />}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="tactile-card p-4">
      <p className="label-caps">{label}</p>
      <p className="font-heading text-3xl font-bold mt-1">{value}</p>
    </div>
  );
}

function AdminShifts({ shifts, volunteers, reload, showPast, setShowPast }) {
  const [open, setOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState({ title: "", starts_at: "", location: "Shelter", capacity: 4, notes: "" });
  const [managingShift, setManagingShift] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/shift-templates");
      setTemplates(data);
    } catch (err) {
      console.error("Failed to load templates:", err);
    }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Auto-refresh every 30s so self-signups from volunteers show up without a manual reload
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        await reload();
        setLastRefreshed(Date.now());
      } catch (err) {
        console.error("Auto-refresh failed", err);
      }
    }, 30000);
    return () => clearInterval(id);
  }, [reload]);

  async function refreshNow() {
    setRefreshing(true);
    try {
      await reload();
      setLastRefreshed(Date.now());
    } catch (err) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }

  async function create() {
    try {
      await api.post("/shifts", { ...f, capacity: Number(f.capacity), starts_at: new Date(f.starts_at).toISOString() });
      toast.success("Shift created"); setOpen(false); setF({ title: "", starts_at: "", location: "Shelter", capacity: 4, notes: "" }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  }
  async function del(id) {
    if (!confirm("Delete this shift and all signups?")) return;
    await api.delete(`/shifts/${id}`); toast.success("Deleted"); reload();
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold">Shifts</h1>
          <p className="text-xs text-muted-foreground mt-1" data-testid="admin-shifts-last-refreshed">
            {showPast ? "Showing all past + upcoming shifts · " : ""}Auto-refreshes every 30s · last updated {new Date(lastRefreshed).toLocaleTimeString()}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none px-3 py-1.5 rounded-full border border-border hover:border-fern transition-colors">
            <Switch checked={!!showPast}
              onCheckedChange={(v) => setShowPast && setShowPast(v)}
              data-testid="admin-shifts-show-past" />
            <span>Show past</span>
          </label>
          <Button onClick={refreshNow} disabled={refreshing} variant="outline" data-testid="admin-shifts-refresh" className="rounded-full">
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button onClick={() => setTemplatesOpen(true)} variant="outline" data-testid="admin-manage-templates" className="rounded-full">
            <Layers className="w-4 h-4 mr-1" strokeWidth={1.5} /> Manage templates
          </Button>
          <Button onClick={() => { loadTemplates(); setGeneratorOpen(true); }} data-testid="admin-generate-shifts"
            className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
            <Calendar className="w-4 h-4 mr-1" strokeWidth={1.5} /> Generate from template
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="admin-new-shift" className="rounded-full bg-fern hover:bg-fern-dark text-white">
            <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> One-off shift
          </Button>
        </div>
      </div>
      <Card className="tactile-card border-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="p-3 label-caps">Title</th>
              <th className="p-3 label-caps">When</th>
              <th className="p-3 label-caps">Slots</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`admin-shift-row-${s.id}`}>
                <td className="p-3 font-medium">
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.title}
                    {s.woof_pack && <Badge className="bg-ink text-white rounded-full text-[10px]">WOOF PACK</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">{s.location}</div>
                </td>
                <td className="p-3">{fmt(s.starts_at)}</td>
                <td className="p-3">{s.signup_count}/{s.capacity}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setManagingShift(s)} data-testid={`admin-manage-shift-${s.id}`}
                    className="rounded-full text-fern hover:text-fern-dark">
                    <Users className="w-4 h-4 mr-1" strokeWidth={1.5} /> Volunteers
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(s.id)} data-testid={`admin-del-shift-${s.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.5} />
                  </Button>
                </td>
              </tr>
            ))}
            {shifts.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No shifts yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>One-off shift (2 hours)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="new-shift-title" className="rounded-xl" /></div>
            <div><Label>Start date & time</Label><Input type="datetime-local" value={f.starts_at} onChange={(e) => setF({ ...f, starts_at: e.target.value })} data-testid="new-shift-starts" className="rounded-xl" /></div>
            <div><Label>Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Capacity</Label><Input type="number" min={1} max={50} value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="new-shift-capacity" className="rounded-xl" /></div>
            <div><Label>Notes (optional)</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button onClick={create} data-testid="new-shift-submit" className="rounded-full bg-fern hover:bg-fern-dark text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemplatesManager open={templatesOpen} onOpenChange={setTemplatesOpen} templates={templates} reload={loadTemplates} />
      <ShiftGenerator open={generatorOpen} onOpenChange={setGeneratorOpen} templates={templates} reloadShifts={reload} openTemplates={() => { setGeneratorOpen(false); setTemplatesOpen(true); }} />
      <ShiftSignupsDialog shift={managingShift} volunteers={volunteers || []} onOpenChange={(o) => !o && setManagingShift(null)} reload={reload} />
    </div>
  );
}

function ShiftSignupsDialog({ shift, volunteers, onOpenChange, reload }) {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [checkoutSig, setCheckoutSig] = useState(null); // {id, name} for hours prompt
  const [checkoutHours, setCheckoutHours] = useState("2");
  const [savingCheckout, setSavingCheckout] = useState(false);

  const loadSignups = useCallback(async () => {
    if (!shift?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/shifts/${shift.id}/signups`);
      setSignups(data.signups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load signups");
    } finally {
      setLoading(false);
    }
  }, [shift?.id]);

  useEffect(() => {
    if (shift?.id) {
      loadSignups();
      setQuery("");
    }
  }, [shift?.id, loadSignups]);

  const signedUpIds = useMemo(() => new Set(signups.map((s) => s.user_id)), [signups]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return (volunteers || [])
      .filter((v) => !signedUpIds.has(v.id))
      .filter((v) => (v.name || "").toLowerCase().includes(q) || (v.email || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, volunteers, signedUpIds]);

  async function addVolunteer(volunteer) {
    if (!shift?.id || adding) return;
    setAdding(true);
    try {
      await api.post(`/admin/shifts/${shift.id}/signups`, { user_id: volunteer.id });
      toast.success(`${volunteer.name} added to this shift`);
      setQuery("");
      await loadSignups();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add volunteer");
    } finally {
      setAdding(false);
    }
  }

  async function removeSignup(signupId, name) {
    if (!confirm(`Remove ${name} from this shift?`)) return;
    try {
      await api.delete(`/admin/shifts/${shift.id}/signups/${signupId}`);
      toast.success("Removed");
      await loadSignups();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove");
    }
  }

  async function adminCheckin(signupId, name) {
    try {
      await api.post(`/admin/shifts/${shift.id}/signups/${signupId}/checkin`);
      toast.success(`${name} checked in`);
      await loadSignups();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't check in");
    }
  }

  function openCheckout(signupId, name) {
    setCheckoutSig({ id: signupId, name });
    setCheckoutHours("2");
  }

  async function submitCheckout() {
    if (!checkoutSig) return;
    const hours = parseFloat(checkoutHours);
    if (Number.isNaN(hours) || hours < 0 || hours > 24) {
      toast.error("Hours must be between 0 and 24");
      return;
    }
    setSavingCheckout(true);
    try {
      const { data } = await api.post(
        `/admin/shifts/${shift.id}/signups/${checkoutSig.id}/checkout`,
        { hours }
      );
      toast.success(`${checkoutSig.name} checked out · +${data.hours}h`);
      setCheckoutSig(null);
      await loadSignups();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't check out");
    } finally {
      setSavingCheckout(false);
    }
  }

  const capacity = shift?.capacity || 0;
  const spotsLeft = Math.max(0, capacity - signups.length);

  return (
    <Dialog open={!!shift} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="shift-signups-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {shift?.title || "Shift"} volunteers
          </DialogTitle>
          <DialogDescription>
            {shift ? `${fmt(shift.starts_at)} · ${signups.length}/${capacity} filled · ${spotsLeft} spot${spotsLeft === 1 ? "" : "s"} left` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="label-caps mb-2">Signed up ({signups.length})</p>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : signups.length === 0 ? (
              <p className="text-sm text-muted-foreground">No volunteers signed up yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="shift-signups-list">
                {signups.map((s) => {
                  const status = s.status || "signed_up";
                  return (
                    <div key={s.id} className="flex items-center justify-between gap-2 p-2.5 rounded-xl border border-border"
                      data-testid={`shift-signup-${s.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{s.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {s.email || "—"}
                          {status !== "signed_up" && ` · ${status.replace("_", " ")}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {status === "signed_up" && (
                          <Button size="sm" variant="outline" onClick={() => adminCheckin(s.id, s.name)}
                            data-testid={`shift-admin-checkin-${s.id}`}
                            className="rounded-full h-8 px-3 text-xs text-fern border-fern hover:bg-fern hover:text-white">
                            Check in
                          </Button>
                        )}
                        {status === "checked_in" && (
                          <Button size="sm" variant="outline" onClick={() => openCheckout(s.id, s.name)}
                            data-testid={`shift-admin-checkout-${s.id}`}
                            className="rounded-full h-8 px-3 text-xs text-terracotta border-terracotta hover:bg-terracotta hover:text-white">
                            Check out
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => removeSignup(s.id, s.name)}
                          data-testid={`shift-remove-signup-${s.id}`}
                          className="text-destructive hover:text-destructive h-8 w-8 p-0">
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-border">
            <p className="label-caps mb-2">Add a volunteer manually</p>
            <p className="text-xs text-muted-foreground mb-2">
              Admin adds bypass the 10-shift active cap and Woof Pack self-serve rules (capacity + approval are still enforced).
            </p>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="rounded-xl h-10"
              data-testid="shift-add-volunteer-search"
            />
            {query && (
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto" data-testid="shift-add-volunteer-results">
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-2">No matching volunteers.</p>
                ) : (
                  filtered.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => addVolunteer(v)}
                      disabled={adding}
                      data-testid={`shift-add-volunteer-${v.id}`}
                      className="w-full text-left p-2.5 rounded-xl hover:bg-fern/5 border border-transparent hover:border-fern/30 transition disabled:opacity-50"
                    >
                      <p className="text-sm font-medium">{v.name}</p>
                      <p className="text-xs text-muted-foreground">{v.email}</p>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="shift-signups-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={!!checkoutSig} onOpenChange={(o) => !o && setCheckoutSig(null)}>
        <DialogContent className="max-w-sm" data-testid="admin-checkout-prompt">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Check out {checkoutSig?.name}</DialogTitle>
            <DialogDescription>How many hours did they work? Adjust if the shift ran short or long.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Hours worked</Label>
            <Input
              type="number"
              step="0.25"
              min="0"
              max="24"
              autoFocus
              value={checkoutHours}
              onChange={(e) => setCheckoutHours(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !savingCheckout) submitCheckout(); }}
              className="rounded-xl text-lg"
              data-testid="admin-checkout-hours"
            />
            <p className="text-xs text-muted-foreground">Defaults to 2h (the standard shift length).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutSig(null)} className="rounded-full">Cancel</Button>
            <Button
              onClick={submitCheckout}
              disabled={savingCheckout}
              data-testid="admin-checkout-submit"
              className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white"
            >
              {savingCheckout ? "Saving…" : "Log hours & check out"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function TemplatesManager({ open, onOpenChange, templates, reload }) {
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({ title: "", default_location: "Shelter", default_capacity: 4, default_notes: "", color: "fern", woof_pack: false });
  function resetForm() {
    setEdit(null);
    setF({ title: "", default_location: "Shelter", default_capacity: 4, default_notes: "", color: "fern", woof_pack: false });
  }
  function startEdit(t) {
    setEdit(t.id);
    setF({ title: t.title, default_location: t.default_location, default_capacity: t.default_capacity, default_notes: t.default_notes || "", color: t.color || "fern", woof_pack: !!t.woof_pack });
  }
  async function save() {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    try {
      const payload = { ...f, default_capacity: Number(f.default_capacity) };
      if (edit) await api.patch(`/shift-templates/${edit}`, payload);
      else await api.post("/shift-templates", payload);
      toast.success("Saved"); resetForm(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function remove(id) {
    if (!confirm("Delete this template? (Existing shifts created from it will remain.)")) return;
    await api.delete(`/shift-templates/${id}`); toast.success("Deleted"); reload();
    if (edit === id) resetForm();
  }
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetForm(); }}>
      <DialogContent className="max-w-2xl" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle>Shift templates</DialogTitle>
          <DialogDescription>Reusable opportunities (e.g. Morning Walk, Yard Cleaning). Use these in the generator to create many shifts at once.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="templates-list">
            {templates.length === 0 && <p className="text-sm text-muted-foreground">No templates yet. Add one →</p>}
            {templates.map((t) => (
              <div key={t.id}
                className={`p-3 rounded-xl border ${edit === t.id ? "border-fern bg-fern/5" : "border-border"} flex justify-between items-start`}
                data-testid={`template-${t.id}`}>
                <button type="button" onClick={() => startEdit(t)} className="text-left flex-1">
                  <p className="font-medium flex items-center gap-2">
                    {t.title}
                    {t.woof_pack && <Badge className="bg-ink text-white rounded-full text-[10px]">WOOF PACK</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">{t.default_location} · {t.default_capacity} slots</p>
                </button>
                <Button size="sm" variant="ghost" onClick={() => remove(t.id)}>
                  <Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.5} />
                </Button>
              </div>
            ))}
          </div>
          <div className="space-y-3 border-l md:pl-4 border-border">
            <p className="label-caps">{edit ? "Edit template" : "New template"}</p>
            <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="tpl-title" className="rounded-xl" placeholder="e.g. Morning Walk" /></div>
            <div><Label>Default location</Label><Input value={f.default_location} onChange={(e) => setF({ ...f, default_location: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Default capacity</Label><Input type="number" min={1} max={50} value={f.default_capacity} onChange={(e) => setF({ ...f, default_capacity: e.target.value })} data-testid="tpl-capacity" className="rounded-xl" /></div>
            <div><Label>Default notes</Label><Textarea value={f.default_notes} onChange={(e) => setF({ ...f, default_notes: e.target.value })} className="rounded-xl" /></div>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer bg-accent/20">
              <Switch checked={f.woof_pack} onCheckedChange={(v) => setF({ ...f, woof_pack: v })} data-testid="tpl-woofpack" />
              <div className="flex-1">
                <p className="text-sm font-medium">Woof Pack template</p>
                <p className="text-xs text-muted-foreground">Only approved volunteers can sign up. No 10-shift cap.</p>
              </div>
            </label>
            <div className="flex gap-2">
              <Button onClick={save} data-testid="tpl-save" className="rounded-full bg-fern hover:bg-fern-dark text-white flex-1">
                {edit ? "Save changes" : "Add template"}
              </Button>
              {edit && <Button variant="outline" onClick={resetForm} className="rounded-full">Cancel</Button>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShiftGenerator({ open, onOpenChange, templates, reloadShifts, openTemplates }) {
  const today = new Date().toISOString().slice(0, 10);
  const plus30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [g, setG] = useState({
    template_id: "",
    start_date: today,
    end_date: plus30,
    start_time: "09:00",
    days_of_week: [0, 1, 2, 3, 4, 5, 6], // Sun..Sat (JS getDay)
    capacity: "",
    location: "",
    notes: "",
  });

  function toggleDay(d) {
    setG((s) => ({
      ...s,
      days_of_week: s.days_of_week.includes(d) ? s.days_of_week.filter((x) => x !== d) : [...s.days_of_week, d].sort(),
    }));
  }

  const previewList = useMemo(() => {
    if (!g.start_date || !g.end_date || !g.start_time) return [];
    const start = new Date(`${g.start_date}T${g.start_time}`);
    const end = new Date(`${g.end_date}T${g.start_time}`);
    if (isNaN(start) || isNaN(end) || end < start) return [];
    const out = [];
    const cur = new Date(start);
    while (cur <= end && out.length < 366) {
      if (g.days_of_week.includes(cur.getDay())) out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [g.start_date, g.end_date, g.start_time, g.days_of_week]);

  async function generate() {
    if (!g.template_id) { toast.error("Pick a template"); return; }
    if (previewList.length === 0) { toast.error("No dates match your selection"); return; }
    try {
      const payload = {
        template_id: g.template_id,
        starts_at_list: previewList.map((d) => d.toISOString()),
        capacity: g.capacity === "" ? null : Number(g.capacity),
        location: g.location === "" ? null : g.location,
        notes: g.notes === "" ? null : g.notes,
        skip_duplicates: true,
      };
      const { data } = await api.post("/shifts/generate", payload);
      toast.success(`Created ${data.created} shift${data.created === 1 ? "" : "s"}${data.skipped ? `, skipped ${data.skipped} duplicate(s)` : ""}`);
      onOpenChange(false);
      reloadShifts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate");
    }
  }

  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="generator-dialog">
        <DialogHeader>
          <DialogTitle>Generate shifts from template</DialogTitle>
          <DialogDescription>Pick a template, a date range, days of the week, and a start time. The app creates one 2-hour shift per matching day.</DialogDescription>
        </DialogHeader>

        {templates.length === 0 ? (
          <div className="text-center py-8 space-y-3">
            <p className="text-sm text-muted-foreground">You need at least one template before generating shifts.</p>
            <Button onClick={openTemplates} className="rounded-full bg-fern hover:bg-fern-dark text-white">Open template manager</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Template</Label>
              <Select value={g.template_id} onValueChange={(v) => setG({ ...g, template_id: v })}>
                <SelectTrigger className="rounded-xl h-11" data-testid="gen-template"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title} ({t.default_capacity} slots)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label><Input type="date" value={g.start_date} onChange={(e) => setG({ ...g, start_date: e.target.value })} data-testid="gen-start-date" className="rounded-xl" /></div>
              <div><Label>End date</Label><Input type="date" value={g.end_date} onChange={(e) => setG({ ...g, end_date: e.target.value })} data-testid="gen-end-date" className="rounded-xl" /></div>
              <div><Label>Time of day</Label><Input type="time" value={g.start_time} onChange={(e) => setG({ ...g, start_time: e.target.value })} data-testid="gen-time" className="rounded-xl" /></div>
            </div>
            <div>
              <Label>Days of the week</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {DOW.map((d, i) => (
                  <button key={d} type="button" onClick={() => toggleDay(i)}
                    data-testid={`gen-day-${i}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition
                      ${g.days_of_week.includes(i) ? "bg-fern text-white border-fern" : "bg-white text-ink border-border hover:bg-accent/40"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <details className="rounded-xl border border-border p-3">
              <summary className="text-sm font-medium cursor-pointer">Optional: override capacity / location / notes</summary>
              <div className="space-y-2 mt-3">
                <div><Label className="text-xs">Capacity override</Label><Input type="number" min={1} max={50} value={g.capacity} onChange={(e) => setG({ ...g, capacity: e.target.value })} placeholder="(use template default)" className="rounded-xl" /></div>
                <div><Label className="text-xs">Location override</Label><Input value={g.location} onChange={(e) => setG({ ...g, location: e.target.value })} placeholder="(use template default)" className="rounded-xl" /></div>
                <div><Label className="text-xs">Notes override</Label><Textarea value={g.notes} onChange={(e) => setG({ ...g, notes: e.target.value })} placeholder="(use template default)" className="rounded-xl" /></div>
              </div>
            </details>
            <div className="bg-accent/40 rounded-xl p-3 text-sm" data-testid="gen-preview">
              <p className="font-medium">Preview</p>
              <p className="text-muted-foreground">
                {previewList.length === 0
                  ? "No matching dates — adjust the range or days above."
                  : `Will create ${previewList.length} shift${previewList.length === 1 ? "" : "s"}` +
                    (previewList.length > 0 ? ` (first: ${previewList[0].toLocaleString()}` +
                      (previewList.length > 1 ? `, last: ${previewList[previewList.length - 1].toLocaleString()}` : "") + `)` : "")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Duplicates (same title + start time) are skipped automatically.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
              <Button onClick={generate} disabled={previewList.length === 0 || !g.template_id} data-testid="gen-submit"
                className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
                Generate {previewList.length > 0 ? `${previewList.length} shifts` : "shifts"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdminAnimals({ animals, species, reload }) {
  const isCat = species === "cat";
  const label = isCat ? "Cats" : "Dogs";
  const labelSingular = isCat ? "cat" : "dog";
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({ name: "", breed: "", age: "", notes: "", description: "", active: true });
  const [noteTarget, setNoteTarget] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [walksOpen, setWalksOpen] = useState(false);
  function openNew() { setEdit(null); setF({ name: "", breed: "", age: "", notes: "", description: "", active: true }); setOpen(true); }
  function openEdit(d) { setEdit(d.id); setF({ name: d.name, breed: d.breed, age: d.age, notes: d.notes, description: d.description || "", active: d.active }); setOpen(true); }
  function openQuickNote(d) { setNoteTarget(d); setNoteText(d.description || ""); }
  async function saveQuickNote() {
    if (!noteTarget) return;
    setNoteBusy(true);
    try {
      await api.patch(`/dogs/${noteTarget.id}/description`, { description: noteText });
      toast.success("Note saved");
      setNoteTarget(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setNoteBusy(false); }
  }
  async function save() {
    try {
      const payload = { ...f, species };
      if (edit) await api.patch(`/dogs/${edit}`, payload);
      else await api.post("/dogs", payload);
      toast.success("Saved"); setOpen(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function del(id) { if (!confirm(`Delete this ${labelSingular}?`)) return; await api.delete(`/dogs/${id}`); reload(); }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="font-heading text-3xl font-bold">{label}</h1>
        <div className="flex gap-2">
          <Button onClick={() => setWalksOpen(true)} variant="outline" data-testid={`admin-${labelSingular}-walks-report`} className="rounded-full">
            <FileDown className="w-4 h-4 mr-1" strokeWidth={1.5} /> Walks report
          </Button>
          <Button onClick={openNew} data-testid={`admin-new-${labelSingular}`} className="rounded-full bg-fern hover:bg-fern-dark text-white">
            <Plus className="w-4 h-4 mr-1" /> Add {labelSingular}
          </Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {animals.map((d) => (
          <Card key={d.id} className="tactile-card border-0 p-4" data-testid={`admin-${labelSingular}-${d.id}`}>
            <div className="flex justify-between items-start mb-2">
              <button type="button" onClick={() => openQuickNote(d)}
                data-testid={`admin-${labelSingular}-note-${d.id}`}
                className="flex items-center gap-3 text-left flex-1 hover:opacity-80">
                {d.photo_url && <img src={d.photo_url} alt={d.name} className="w-12 h-12 rounded-full object-cover" />}
                <div>
                  <p className="font-heading font-semibold">
                    {d.name}
                    {!d.active && <span className="text-xs text-muted-foreground ml-1">(inactive)</span>}
                    {d.shelterluv_id && <Badge className="ml-2 bg-fern/10 text-fern rounded-full text-[10px]">ShelterLuv</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {d.breed} {d.age && `· ${d.age}`}
                    {d.shelterluv_status && <span className="ml-1">· {d.shelterluv_status}</span>}
                  </p>
                </div>
              </button>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(d)}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => del(d.id)}><Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.5} /></Button>
              </div>
            </div>
            {d.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-2 pl-1">{d.description}</p>
            )}
          </Card>
        ))}
        {animals.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No {label.toLowerCase()} added yet.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit ? "Edit" : "New"} {labelSingular}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`${labelSingular}-name`} className="rounded-xl" /></div>
            <div><Label>Breed</Label><Input value={f.breed} onChange={(e) => setF({ ...f, breed: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Age</Label><Input value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} className="rounded-xl" /></div>
            <div>
              <Label>Description (shown to volunteers)</Label>
              <Textarea value={f.description} maxLength={2000}
                onChange={(e) => setF({ ...f, description: e.target.value })}
                placeholder={`A short note volunteers see when they tap on this ${labelSingular}: personality, quirks, handling tips…`}
                data-testid={`${labelSingular}-description`}
                className="rounded-xl min-h-[90px]" />
            </div>
            <div><Label>Internal notes (admin only)</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-xl" /></div>
            <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> <span className="text-sm">Active (volunteers can log this {labelSingular})</span></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid={`${labelSingular}-submit`} className="rounded-full bg-fern hover:bg-fern-dark text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!noteTarget} onOpenChange={(o) => !o && setNoteTarget(null)}>
        <DialogContent data-testid="admin-quick-note-dialog">
          <DialogHeader>
            <DialogTitle>Quick note · {noteTarget?.name}</DialogTitle>
            <DialogDescription>
              This description is visible to volunteers when they tap on {noteTarget?.name} in the app.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            maxLength={2000}
            placeholder={`Personality, quirks, handling tips…`}
            data-testid="admin-quick-note-input"
            className="rounded-xl min-h-[130px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteTarget(null)} className="rounded-full">Cancel</Button>
            <Button onClick={saveQuickNote} disabled={noteBusy} data-testid="admin-quick-note-save"
              className="rounded-full bg-fern hover:bg-fern-dark text-white">
              {noteBusy ? "Saving…" : "Save note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WalksReportDialog open={walksOpen} onOpenChange={setWalksOpen} species={species} label={label} />
    </div>
  );
}

function WalksReportDialog({ open, onOpenChange, species, label }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const monthAgoISO = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgoISO);
  const [to, setTo] = useState(todayISO);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [groupBy, setGroupBy] = useState("animal"); // "animal" | "volunteer"
  const [showUnwalked, setShowUnwalked] = useState(false);

  async function run() {
    if (!from || !to) { toast.error("Pick both dates"); return; }
    setBusy(true);
    try {
      const { data: d } = await api.get(`/admin/reports/animal-walks?from=${from}&to=${to}&species=${species}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load report");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { if (open) run(); /* auto-load */ }, [open, species]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredAnimals = useMemo(() => {
    const rows = data?.by_animal?.rows || data?.rows || [];
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.animal_name || "").toLowerCase().includes(q) ||
      (r.walkers || []).some((w) => (w.name || "").toLowerCase().includes(q) || (w.email || "").toLowerCase().includes(q))
    );
  }, [data, filter]);

  const filteredVolunteers = useMemo(() => {
    const rows = data?.by_volunteer?.rows || [];
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.animals || []).some((a) => (a.animal_name || "").toLowerCase().includes(q))
    );
  }, [data, filter]);

  const filteredUnwalked = useMemo(() => {
    const rows = data?.by_animal?.unwalked || [];
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.animal_name || "").toLowerCase().includes(q) || (r.breed || "").toLowerCase().includes(q)
    );
  }, [data, filter]);

  function downloadCsv() {
    if (!data) return;
    let lines = [];
    let filename = "";
    if (showUnwalked) {
      lines = [["Animal", "Breed"].join(",")];
      (data.by_animal?.unwalked || []).forEach((r) => {
        lines.push([
          `"${(r.animal_name || "").replace(/"/g, '""')}"`,
          `"${(r.breed || "").replace(/"/g, '""')}"`,
        ].join(","));
      });
      filename = `${species}-unwalked_${from}_to_${to}.csv`;
    } else if (groupBy === "volunteer") {
      lines = [["Volunteer", "Email", "Animal", "Walks", "Last walked"].join(",")];
      (data.by_volunteer?.rows || []).forEach((v) => {
        v.animals.forEach((a) => {
          lines.push([
            `"${(v.name || "").replace(/"/g, '""')}"`,
            `"${(v.email || "").replace(/"/g, '""')}"`,
            `"${(a.animal_name || "").replace(/"/g, '""')}"`,
            a.walks,
            a.last_walked_at ? a.last_walked_at.slice(0, 10) : "",
          ].join(","));
        });
      });
      filename = `${species}-walks-by-volunteer_${from}_to_${to}.csv`;
    } else {
      lines = [["Animal", "Breed", "Volunteer", "Email", "Walks", "Last walked"].join(",")];
      (data.by_animal?.rows || data.rows || []).forEach((row) => {
        if (row.walkers.length === 0) {
          lines.push([`"${row.animal_name}"`, `"${row.breed}"`, "", "", 0, ""].join(","));
        } else {
          row.walkers.forEach((w) => {
            lines.push([
              `"${(row.animal_name || "").replace(/"/g, '""')}"`,
              `"${(row.breed || "").replace(/"/g, '""')}"`,
              `"${(w.name || "").replace(/"/g, '""')}"`,
              `"${(w.email || "").replace(/"/g, '""')}"`,
              w.walks,
              w.last_walked_at ? w.last_walked_at.slice(0, 10) : "",
            ].join(","));
          });
        }
      });
      filename = `${species}-walks-by-animal_${from}_to_${to}.csv`;
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  const walkWord = species === "cat" ? "cuddles" : "walks";
  const walkerWord = species === "cat" ? "cuddler" : "walker";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="walks-report-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{label} {walkWord} report</DialogTitle>
          <DialogDescription>
            Which volunteers have {walkWord === "cuddles" ? "cuddled" : "walked"} which {species}s and how often. Based on completed shift check-outs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="rounded-xl h-11" data-testid="walks-report-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="rounded-xl h-11" data-testid="walks-report-to" />
            </div>
            <Button onClick={run} disabled={busy} data-testid="walks-report-run"
              className="rounded-full bg-fern hover:bg-fern-dark text-white h-11">
              {busy ? "Loading…" : "Search"}
            </Button>
          </div>

          {data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="tactile-card p-3">
                  <p className="label-caps">Total {walkWord}</p>
                  <p className="font-heading text-2xl font-semibold" data-testid="walks-report-total">{data.total_walks}</p>
                </div>
                <div className="tactile-card p-3">
                  <p className="label-caps">{label} {walkWord === "cuddles" ? "cuddled" : "walked"}</p>
                  <p className="font-heading text-2xl font-semibold">{data.animal_count}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setShowUnwalked(true); }}
                  className={`tactile-card p-3 text-left transition hover:shadow-md ${showUnwalked ? "ring-2 ring-terracotta" : ""} ${(data.by_animal?.unwalked_count || 0) > 0 ? "cursor-pointer" : "cursor-default opacity-70"}`}
                  data-testid="walks-unwalked-card"
                >
                  <p className="label-caps text-terracotta">Un{walkWord === "cuddles" ? "cuddled" : "walked"}</p>
                  <p className="font-heading text-2xl font-semibold text-terracotta">{data.by_animal?.unwalked_count ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground">tap to view</p>
                </button>
                <div className="tactile-card p-3">
                  <p className="label-caps">Volunteers</p>
                  <p className="font-heading text-2xl font-semibold">{data.by_volunteer?.count ?? 0}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 items-center justify-between">
                <div className="inline-flex rounded-full bg-accent/40 p-1" data-testid="walks-report-toggle">
                  <button
                    type="button"
                    onClick={() => { setGroupBy("animal"); setShowUnwalked(false); }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition ${(!showUnwalked && groupBy === "animal") ? "bg-white shadow text-fern" : "text-muted-foreground"}`}
                    data-testid="walks-report-by-animal"
                  >
                    By {species}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setGroupBy("volunteer"); setShowUnwalked(false); }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition ${(!showUnwalked && groupBy === "volunteer") ? "bg-white shadow text-fern" : "text-muted-foreground"}`}
                    data-testid="walks-report-by-volunteer"
                  >
                    By volunteer
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowUnwalked(true)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition ${showUnwalked ? "bg-white shadow text-terracotta" : "text-muted-foreground"}`}
                    data-testid="walks-report-unwalked"
                  >
                    Un{walkWord === "cuddles" ? "cuddled" : "walked"} ({data.by_animal?.unwalked_count ?? 0})
                  </button>
                </div>
                <Input value={filter} onChange={(e) => setFilter(e.target.value)}
                  placeholder={showUnwalked ? `Filter ${species}…` : `Filter ${species} or volunteer…`}
                  className="rounded-full h-9 max-w-xs"
                  data-testid="walks-report-filter" />
                <Button onClick={downloadCsv} variant="outline" disabled={!data}
                  data-testid="walks-report-csv" className="rounded-full">
                  <FileDown className="w-4 h-4 mr-1" strokeWidth={1.5} /> Download CSV
                </Button>
              </div>

              {showUnwalked ? (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="walks-report-list">
                  {filteredUnwalked.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6">
                      Every active {species} got at least one {walkWord === "cuddles" ? "cuddle" : "walk"} in this window 🎉
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {filteredUnwalked.map((a) => (
                        <Card key={a.animal_id} className="tactile-card border-0 p-3 border-l-4 border-terracotta/60" data-testid={`walks-unwalked-${a.animal_id}`}>
                          <p className="font-heading font-semibold">{a.animal_name}</p>
                          {a.breed && <p className="text-xs text-muted-foreground">{a.breed}</p>}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ) : groupBy === "volunteer" ? (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="walks-report-list">
                  {filteredVolunteers.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6">
                      No {walkWord} logged in this range.
                    </p>
                  ) : filteredVolunteers.map((v) => (
                    <Card key={v.user_id} className="tactile-card border-0 p-3" data-testid={`walks-volunteer-${v.user_id}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <p className="font-heading font-semibold">{v.name}</p>
                          <p className="text-xs text-muted-foreground">{v.email}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-heading text-xl font-semibold">{v.total_walks}</p>
                          <p className="text-xs text-muted-foreground">{v.unique_animals} {species}{v.unique_animals === 1 ? "" : "s"}</p>
                        </div>
                      </div>
                      <div className="space-y-1 pl-2 border-l-2 border-fern/20">
                        {v.animals.map((a) => (
                          <div key={a.animal_id} className="flex items-center justify-between gap-2 text-sm">
                            <p className="truncate">{a.animal_name}</p>
                            <div className="text-right shrink-0 text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">{a.walks}×</span>
                              {a.last_walked_at && <span className="ml-2 text-[11px]">last {a.last_walked_at.slice(0, 10)}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="walks-report-list">
                  {filteredAnimals.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6">
                      {(data.by_animal?.rows || data.rows || []).length === 0 ? `No ${walkWord} logged in this range.` : "No matches for that filter."}
                    </p>
                  ) : filteredAnimals.map((row) => (
                    <Card key={row.animal_id} className="tactile-card border-0 p-3" data-testid={`walks-row-${row.animal_id}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <p className="font-heading font-semibold">
                            {row.animal_name}
                            {!row.active && <span className="text-xs text-muted-foreground font-normal ml-1">(inactive)</span>}
                          </p>
                          {row.breed && <p className="text-xs text-muted-foreground">{row.breed}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-heading text-xl font-semibold">{row.total_walks}</p>
                          <p className="text-xs text-muted-foreground">{row.unique_walkers} {walkerWord}{row.unique_walkers === 1 ? "" : "s"}</p>
                        </div>
                      </div>
                      <div className="space-y-1 pl-2 border-l-2 border-fern/20">
                        {row.walkers.map((w) => (
                          <div key={w.user_id} className="flex items-center justify-between gap-2 text-sm">
                            <div className="min-w-0 flex-1">
                              <p className="truncate">{w.name}</p>
                              <p className="text-[11px] text-muted-foreground truncate">{w.email}</p>
                            </div>
                            <div className="text-right shrink-0 text-xs text-muted-foreground">
                              <p className="font-medium text-foreground">{w.walks}×</p>
                              {w.last_walked_at && <p className="text-[11px]">last {w.last_walked_at.slice(0, 10)}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="walks-report-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminBulletins({ bulletins, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", body: "", pinned: false });
  async function create() {
    try { await api.post("/bulletins", f); toast.success("Posted"); setOpen(false); setF({ title: "", body: "", pinned: false }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function del(id) { if (!confirm("Delete?")) return; await api.delete(`/bulletins/${id}`); reload(); }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="font-heading text-3xl font-bold">Bulletin board</h1>
        <Button onClick={() => setOpen(true)} data-testid="admin-new-bulletin" className="rounded-full bg-fern hover:bg-fern-dark text-white">
          <Plus className="w-4 h-4 mr-1" /> New post
        </Button>
      </div>
      <div className="space-y-3">
        {bulletins.map((b) => (
          <Card key={b.id} className="tactile-card border-0 p-5">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  {b.pinned && <Pin className="w-4 h-4 text-terracotta" strokeWidth={1.5} />}
                  <h3 className="font-heading font-semibold">{b.title}</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{fmt(b.created_at)} · {b.author}</p>
                <p className="text-sm whitespace-pre-wrap">{b.body}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => del(b.id)}><Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.5} /></Button>
            </div>
          </Card>
        ))}
        {bulletins.length === 0 && <p className="text-sm text-muted-foreground">No posts yet.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New bulletin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="bulletin-title" className="rounded-xl" /></div>
            <div><Label>Body</Label><Textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="bulletin-body" className="rounded-xl min-h-[140px]" /></div>
            <label className="flex items-center gap-2"><Switch checked={f.pinned} onCheckedChange={(v) => setF({ ...f, pinned: v })} /> <span className="text-sm">Pin to top</span></label>
          </div>
          <DialogFooter><Button onClick={create} data-testid="bulletin-submit" className="rounded-full bg-fern hover:bg-fern-dark text-white">Post</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IssueNotes({ issue, reload }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = issue.notes || [];

  async function submit() {
    const v = text.trim();
    if (!v) return;
    setBusy(true);
    try {
      await api.post(`/issues/${issue.id}/notes`, { text: v });
      setText("");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save note");
    } finally {
      setBusy(false);
    }
  }

  async function removeNote(noteId) {
    try {
      await api.delete(`/issues/${issue.id}/notes/${noteId}`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete note");
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid={`issue-notes-toggle-${issue.id}`}
        className="text-xs font-medium text-fern hover:text-fern-dark inline-flex items-center gap-1">
        <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
        {open ? "Hide notes" : `Notes${notes.length ? ` (${notes.length})` : ""}`}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {notes.length === 0 && <p className="text-xs text-muted-foreground italic">No notes yet. Add the first one below.</p>}
          {notes.map((n) => (
            <div key={n.id} className="rounded-xl bg-sand/60 border border-border/60 p-3 text-sm"
              data-testid={`issue-note-${issue.id}-${n.id}`}>
              <div className="flex justify-between items-start gap-2 mb-1">
                <p className="text-xs text-muted-foreground">{n.author_name} · {fmt(n.created_at)}</p>
                <button type="button" onClick={() => removeNote(n.id)}
                  aria-label="Delete note"
                  data-testid={`issue-note-delete-${issue.id}-${n.id}`}
                  className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
              <p className="whitespace-pre-wrap text-ink">{n.text}</p>
            </div>
          ))}
          <div className="flex gap-2 items-start">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Describe what was done, next steps, vet contact, etc."
              rows={2}
              maxLength={2000}
              data-testid={`issue-note-input-${issue.id}`}
              className="rounded-xl min-h-[64px] text-sm"
            />
            <Button size="sm" onClick={submit} disabled={busy || !text.trim()}
              data-testid={`issue-note-submit-${issue.id}`}
              className="rounded-full bg-fern hover:bg-fern-dark text-white shrink-0">
              {busy ? "Saving…" : "Add note"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminIssues({ issues, reload }) {
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [busy, setBusy] = useState(false);
  const resolvedCount = issues.filter((i) => i.status === "resolved").length;

  async function setStatus(id, status) {
    await api.patch(`/issues/${id}/status?status=${status}`);
    toast.success("Updated"); reload();
  }

  async function confirmDelete() {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      await api.delete(`/issues/${confirmTarget.id}`);
      toast.success("Issue deleted");
      setConfirmTarget(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  }

  async function confirmBulkDelete() {
    setBusy(true);
    try {
      const { data } = await api.post("/issues/bulk-delete", { status: "resolved" });
      toast.success(`Deleted ${data.deleted_count} resolved report${data.deleted_count === 1 ? "" : "s"}`);
      setConfirmBulk(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <h1 className="font-heading text-3xl font-bold">Animal issue reports</h1>
        {resolvedCount > 0 && (
          <Button
            variant="outline"
            onClick={() => setConfirmBulk(true)}
            data-testid="issues-bulk-delete"
            className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10">
            <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
            Delete all resolved ({resolvedCount})
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {issues.map((i) => (
          <Card key={i.id} className="tactile-card border-0 p-5" data-testid={`admin-issue-${i.id}`}>
            <div className="flex justify-between items-start mb-2 gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading font-semibold">{i.title}</h3>
                  <Badge className={`rounded-full ${severityClass(i.severity)} text-white`}>{i.severity}</Badge>
                  <Badge className={`rounded-full ${issueStatusClass(i.status)} text-white`}>{i.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{i.reporter_name} · {i.dog_name || "General"} · {fmt(i.created_at)}</p>
                <p className="text-sm">{i.description}</p>
                {i.photo_path && (
                  <button type="button" onClick={() => openProtectedImage(i.photo_path)}
                    className="inline-block mt-2 text-xs text-fern underline" data-testid={`view-photo-${i.id}`}>
                    View photo
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="outline" onClick={() => setStatus(i.id, "in_progress")} data-testid={`issue-progress-${i.id}`} className="rounded-full">In progress</Button>
                <Button size="sm" onClick={() => setStatus(i.id, "resolved")} data-testid={`issue-resolve-${i.id}`} className="rounded-full bg-fern hover:bg-fern-dark text-white">Resolve</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmTarget(i)}
                  data-testid={`issue-delete-${i.id}`} aria-label={`Delete ${i.title}`}
                  className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10">
                  <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Delete
                </Button>
              </div>
            </div>
            <IssueNotes issue={i} reload={reload} />
          </Card>
        ))}
        {issues.length === 0 && <p className="text-sm text-muted-foreground">No issues reported.</p>}
      </div>

      <Dialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <DialogContent data-testid="issue-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this issue?</DialogTitle>
            <DialogDescription>
              This permanently removes the report &quot;<span className="font-medium text-ink">{confirmTarget?.title}</span>&quot;
              from the system. Use this to clean up test reports or resolved incidents you no longer need on file.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmTarget(null)} className="rounded-full">Cancel</Button>
            <Button onClick={confirmDelete} disabled={busy} data-testid="issue-delete-confirm"
              className="rounded-full bg-destructive hover:bg-destructive/90 text-white">
              {busy ? "Deleting…" : "Yes, delete report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <DialogContent data-testid="issue-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete all resolved reports?</DialogTitle>
            <DialogDescription>
              This will permanently remove <span className="font-medium text-ink">{resolvedCount}</span> report
              {resolvedCount === 1 ? "" : "s"} that are marked &ldquo;resolved&rdquo;, along with any notes attached to them.
              Open and in-progress reports are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulk(false)} className="rounded-full">Cancel</Button>
            <Button onClick={confirmBulkDelete} disabled={busy} data-testid="issue-bulk-delete-confirm"
              className="rounded-full bg-destructive hover:bg-destructive/90 text-white">
              {busy ? "Deleting…" : `Yes, delete ${resolvedCount} resolved`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LaunchEmailEditor() {
  const [tpl, setTpl] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/launch-email");
      setTpl(data);
      setSubject(data.subject);
      setBody(data.body_html);
      setDirty(false);
    } catch (e) {
      console.error("Failed to load launch template", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doPreview = useCallback(async () => {
    try {
      const { data } = await api.post("/settings/launch-email/preview", { subject, body_html: body });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  }, [subject, body]);

  useEffect(() => {
    if (!open) return;
    doPreview();
  }, [open, doPreview]);

  async function save() {
    setBusy(true);
    try {
      await api.put("/settings/launch-email", { subject, body_html: body });
      toast.success("Launch email template saved");
      await load();
      await doPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  async function reset() {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/launch-email", { reset: true });
      setSubject(data.subject);
      setBody(data.body_html);
      setDirty(false);
      toast.success("Reset to the default template");
      await doPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  }

  async function sendTest() {
    const target = (testTo || "").trim();
    if (!target || !target.includes("@")) {
      toast.error("Enter a valid email address to receive the test");
      return;
    }
    if (!body.includes("{{link}}")) {
      toast.error("Body must include {{link}} — a real launch email is unusable without it");
      return;
    }
    setTestBusy(true);
    try {
      const { data } = await api.post("/settings/launch-email/send-test", {
        to: target,
        subject,
        body_html: body,
      });
      if (data.used_sample_link) {
        toast.success(
          `Sent to ${data.sent_to} — this address isn't a registered volunteer, so the link is a sample. Full flow works when you Send test to a real volunteer.`,
          { duration: 8000 }
        );
      } else {
        toast.success(`Sent proof copy to ${data.sent_to} — check that inbox`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally { setTestBusy(false); }
  }

  if (!tpl) return null;

  return (
    <div className="rounded-xl border border-border bg-sand/50 p-3" data-testid="launch-email-editor-block">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-ink">Email content</p>
          <p className="text-xs text-muted-foreground truncate">
            Subject: <span className="text-ink">{tpl.subject}</span>
            {tpl.is_custom && <span className="ml-2 text-fern">· custom</span>}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="launch-email-open-editor"
          className="rounded-full"
        >
          Preview &amp; edit
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto"
          data-testid="launch-email-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle>Launch email — preview &amp; edit</DialogTitle>
            <DialogDescription>
              Edit the subject and body. Use <code className="bg-muted px-1 rounded text-xs">{`{{first_name}}`}</code>,{" "}
              <code className="bg-muted px-1 rounded text-xs">{`{{name}}`}</code>, and{" "}
              <code className="bg-muted px-1 rounded text-xs">{`{{link}}`}</code> as variables.
              The body must include <code className="bg-muted px-1 rounded text-xs">{`{{link}}`}</code>.
            </DialogDescription>
          </DialogHeader>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Editor */}
            <div className="space-y-3">
              <div>
                <Label>Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => { setSubject(e.target.value); setDirty(true); }}
                  maxLength={200}
                  data-testid="launch-email-subject"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Body (HTML allowed)</Label>
                <Textarea
                  value={body}
                  onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                  maxLength={20000}
                  rows={22}
                  data-testid="launch-email-body"
                  className="rounded-xl font-mono text-xs leading-relaxed"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  {body.length} / 20,000 characters ·{" "}
                  {body.includes("{{link}}") ? (
                    <span className="text-fern">contains &#123;&#123;link&#125;&#125; ✓</span>
                  ) : (
                    <span className="text-destructive">missing &#123;&#123;link&#125;&#125; — required</span>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <Button
                  onClick={save}
                  disabled={!dirty || busy || !body.includes("{{link}}")}
                  data-testid="launch-email-save"
                  className="rounded-full bg-fern hover:bg-fern-dark text-white"
                >
                  {busy ? "Saving…" : "Save changes"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={doPreview}
                  data-testid="launch-email-refresh-preview"
                  className="rounded-full"
                >
                  Refresh preview
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={reset}
                  disabled={busy}
                  data-testid="launch-email-reset"
                  className="rounded-full text-muted-foreground"
                >
                  Reset to default
                </Button>
              </div>

              {/* Send-test row — proof the current draft against a real inbox */}
              <div className="rounded-xl bg-fern/5 border border-fern/20 p-3 space-y-2">
                <p className="text-xs font-medium text-fern-dark">
                  Send a proof copy to a single address
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Uses the current draft (no need to save first). If the address matches a registered
                  volunteer, they get a real password-set link — otherwise a sample link is used.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Input
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="launch-email-test-to"
                    className="rounded-full h-10 flex-1 min-w-[180px]"
                  />
                  <Button
                    type="button"
                    onClick={sendTest}
                    disabled={testBusy || !testTo.trim()}
                    data-testid="launch-email-send-test"
                    className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white whitespace-nowrap"
                  >
                    {testBusy ? "Sending…" : "Send proof"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <p className="label-caps">Preview <span className="ml-1 text-muted-foreground normal-case tracking-normal">(sample: {preview?.sample_name || "…"})</span></p>
              <div className="rounded-xl border border-border bg-white overflow-hidden">
                <div className="border-b border-border px-4 py-2 text-xs bg-sand">
                  <p className="text-muted-foreground">Subject:</p>
                  <p className="text-ink font-medium" data-testid="launch-email-preview-subject">{preview?.subject || "—"}</p>
                </div>
                <div
                  className="p-4 text-sm max-h-[520px] overflow-y-auto"
                  data-testid="launch-email-preview-body"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview?.body_html || "<em>Loading preview…</em>") }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                <code className="bg-muted px-1 rounded">&#123;&#123;link&#125;&#125;</code> in the preview points to a sample URL. In the real email each volunteer gets their own unique password-set link.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EmailSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    provider: "resend",
    sender_name: "", sender_email: "", reply_to: "",
    smtp_host: "", smtp_port: 587, smtp_username: "",
    smtp_password: "", smtp_use_tls: true,
  });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/email");
      setCfg(data);
      setForm({
        provider: data.provider || "resend",
        sender_name: data.sender_name || "",
        sender_email: data.sender_email || "",
        reply_to: data.reply_to || "",
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || 587,
        smtp_username: data.smtp_username || "",
        smtp_password: "",  // never pre-fill — server masks it
        smtp_use_tls: data.smtp_use_tls !== false,
      });
      setDirty(false);
    } catch (e) {
      console.error("Failed to load email settings", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function edit(key, value) {
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Auto-fill Google Workspace preset when switching to SMTP tab if fields are blank
      if (key === "provider" && value === "smtp") {
        if (!next.smtp_host) next.smtp_host = "smtp.gmail.com";
        if (!next.smtp_port) next.smtp_port = 587;
        if (next.smtp_use_tls === undefined || next.smtp_use_tls === null) next.smtp_use_tls = true;
        if (!next.smtp_username && next.sender_email) next.smtp_username = next.sender_email;
      }
      // Keep SMTP username auto-synced with sender_email while it's still empty
      if (key === "sender_email" && f.provider === "smtp" && !f.smtp_username) {
        next.smtp_username = value;
      }
      return next;
    });
    setDirty(true);
  }

  function applyGooglePreset() {
    if (!cfg) return;
    const g = cfg.google_workspace_preset;
    setForm((f) => ({
      ...f,
      provider: "smtp",
      smtp_host: g.smtp_host,
      smtp_port: g.smtp_port,
      smtp_use_tls: g.smtp_use_tls,
      // If sender_email is already a Google Workspace address, sync SMTP username to it
      smtp_username: f.smtp_username || f.sender_email || "",
    }));
    setDirty(true);
  }

  async function save() {
    setBusy(true);
    try {
      // Only send smtp_password if user actually typed a new one
      const payload = { ...form };
      if (!payload.smtp_password) delete payload.smtp_password;
      const { data } = await api.put("/settings/email", payload);
      if (data && data.warning) {
        toast.warning(data.warning, { duration: 7000 });
      } else {
        toast.success("Email settings saved");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  async function usePlatform() {
    if (!cfg) return;
    setForm((f) => ({
      ...f,
      provider: "resend",
      sender_name: cfg.platform_default.sender_name,
      sender_email: cfg.platform_default.sender_email,
    }));
    setDirty(true);
  }

  async function sendTest() {
    if (dirty) {
      toast.warning("You have unsaved changes — click Save first, then Send test.", { duration: 6000 });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/settings/email/test", { to: testTo || undefined });
      toast.success(`Test sent via ${data.provider}${data.resend_id ? ` (${data.resend_id})` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setBusy(false); }
  }

  if (!cfg) return null;

  const isSMTP = form.provider === "smtp";
  const usingPlatform = !isSMTP && cfg.sender_email === cfg.platform_default.sender_email && cfg.source === "settings";

  let badgeText, badgeClass;
  if (cfg.provider === "smtp" && cfg.smtp_password_set) {
    badgeText = "Google Workspace SMTP";
    badgeClass = "bg-fern/10 text-fern-dark";
  } else if (cfg.provider === "smtp") {
    badgeText = "SMTP · App Password needed";
    badgeClass = "bg-amber-50 text-amber-800";
  } else if (usingPlatform) {
    badgeText = "Platform sender · needs shelterfocus.com verified";
    badgeClass = "bg-amber-50 text-amber-800";
  } else {
    badgeText = "Branded sender · needs DNS verification";
    badgeClass = "bg-amber-50 text-amber-800";
  }

  return (
    <Card className="tactile-card border-0 p-5" data-testid="email-settings-card">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-fern/10 text-fern shrink-0">
            <Mail className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold">Email settings</h3>
            <p className="text-sm text-muted-foreground">
              How Shelter Focus sends launch, reset, and notification emails to your volunteers.
            </p>
          </div>
        </div>
        <span
          className={`text-xs px-3 py-1 rounded-full font-medium ${badgeClass}`}
          data-testid="email-settings-source"
        >
          {badgeText}
        </span>
      </div>

      {/* Provider tabs */}
      <div className="grid grid-cols-2 gap-2 mb-4 p-1 rounded-full bg-sand" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={form.provider === "resend"}
          onClick={() => edit("provider", "resend")}
          data-testid="email-provider-resend"
          className={`rounded-full h-9 text-sm font-medium transition-colors ${form.provider === "resend" ? "bg-white shadow-sm text-ink" : "text-muted-foreground"}`}
        >
          Resend (API)
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={form.provider === "smtp"}
          onClick={() => edit("provider", "smtp")}
          data-testid="email-provider-smtp"
          className={`rounded-full h-9 text-sm font-medium transition-colors ${form.provider === "smtp" ? "bg-white shadow-sm text-ink" : "text-muted-foreground"}`}
        >
          Google Workspace SMTP
        </button>
      </div>

      {isSMTP ? (
        <>
          <div className="rounded-xl bg-fern/5 border border-fern/20 p-3 text-xs text-ink mb-3">
            <p className="font-medium mb-1 text-fern-dark">Recommended for shelters without DNS access</p>
            <p className="text-muted-foreground">
              Uses your existing Google Workspace inbox to send. No DNS setup needed — Google already handles it.
              Requires a 16-character App Password: generate one at{" "}
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline text-fern">
                myaccount.google.com/apppasswords
              </a>{" "}
              (2-Step Verification must be on).
            </p>
            <button type="button" onClick={applyGooglePreset}
              data-testid="email-google-preset"
              className="mt-2 text-fern underline text-xs">
              Use Google Workspace preset (smtp.gmail.com:587 TLS)
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <Label>Display name (shown to volunteers)</Label>
              <Input value={form.sender_name} onChange={(e) => edit("sender_name", e.target.value)}
                placeholder="Satchel's Last Resort" data-testid="email-settings-name" className="rounded-xl" />
            </div>
            <div>
              <Label>Sender email (must be a Google Workspace address)</Label>
              <Input value={form.sender_email} onChange={(e) => edit("sender_email", e.target.value)}
                placeholder="michalanne@satchelslastresort.org" data-testid="email-settings-from" className="rounded-xl" />
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3 mb-3">
            <div className="md:col-span-2">
              <Label>SMTP host</Label>
              <Input value={form.smtp_host} onChange={(e) => edit("smtp_host", e.target.value)}
                placeholder="smtp.gmail.com" data-testid="email-smtp-host" className="rounded-xl" />
            </div>
            <div>
              <Label>Port</Label>
              <Input type="number" value={form.smtp_port} onChange={(e) => edit("smtp_port", parseInt(e.target.value || "0", 10))}
                data-testid="email-smtp-port" className="rounded-xl" />
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Switch checked={!!form.smtp_use_tls} onCheckedChange={(v) => edit("smtp_use_tls", v)}
                data-testid="email-smtp-tls" />
              <Label className="mb-0">Use TLS</Label>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <Label>SMTP username (Google email address)</Label>
              <Input value={form.smtp_username} onChange={(e) => edit("smtp_username", e.target.value)}
                placeholder="michalanne@satchelslastresort.org" data-testid="email-smtp-username" className="rounded-xl" />
            </div>
            <div>
              <Label>
                App Password (16 characters, no spaces)
                {cfg.smtp_password_set && <span className="text-xs text-fern ml-2">· saved</span>}
              </Label>
              <div className="flex gap-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={form.smtp_password}
                  onChange={(e) => edit("smtp_password", e.target.value)}
                  placeholder={cfg.smtp_password_set ? "•••••••••••••••• (paste new to change)" : "abcd efgh ijkl mnop"}
                  data-testid="email-smtp-password"
                  className="rounded-xl font-mono"
                />
                <Button type="button" variant="outline" onClick={() => setShowPassword((v) => !v)}
                  data-testid="email-smtp-show-password" className="rounded-full">
                  {showPassword ? "Hide" : "Show"}
                </Button>
              </div>
            </div>
          </div>

          <div>
            <Label>Reply-to (optional)</Label>
            <Input value={form.reply_to} onChange={(e) => edit("reply_to", e.target.value)}
              placeholder="michalanne@satchelslastresort.org" data-testid="email-settings-reply-to" className="rounded-xl" />
          </div>
        </>
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-3 mb-3">
            <div>
              <Label>Display name</Label>
              <Input value={form.sender_name} onChange={(e) => edit("sender_name", e.target.value)}
                placeholder="Satchel's Last Resort" data-testid="email-settings-name" className="rounded-xl" />
            </div>
            <div>
              <Label>Sender email</Label>
              <Input value={form.sender_email} onChange={(e) => edit("sender_email", e.target.value)}
                placeholder="volunteers@yourdomain.org" data-testid="email-settings-from" className="rounded-xl" />
            </div>
            <div>
              <Label>Reply-to (optional)</Label>
              <Input value={form.reply_to} onChange={(e) => edit("reply_to", e.target.value)}
                placeholder="michalanne@satchelslastresort.org" data-testid="email-settings-reply-to" className="rounded-xl" />
            </div>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 mb-3">
            <p className="font-medium mb-1">Resend requires DNS verification for your sender domain.</p>
            <p>
              If your registrar (Wix, etc.) doesn&apos;t let you add DNS records, switch to{" "}
              <button type="button" onClick={() => edit("provider", "smtp")} className="underline font-medium">
                Google Workspace SMTP
              </button>{" "}
              — works with your existing Google Workspace account, no DNS needed.
            </p>
          </div>
        </>
      )}

      <div className="flex flex-wrap gap-2 items-center mt-3">
        <Button onClick={save} disabled={!dirty || busy}
          data-testid="email-settings-save"
          className="rounded-full bg-fern hover:bg-fern-dark text-white">
          {busy ? "Saving…" : "Save"}
        </Button>
        {!isSMTP && !usingPlatform && (
          <Button type="button" variant="outline" onClick={usePlatform}
            data-testid="email-settings-use-platform" className="rounded-full">
            Use platform sender
          </Button>
        )}
        <div className="flex-1 flex flex-wrap items-center gap-2 min-w-[220px]">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)}
            placeholder="Send test to…"
            data-testid="email-settings-test-to"
            className="rounded-full h-10 flex-1 min-w-[160px]" />
          <Button type="button" variant="outline" onClick={sendTest} disabled={busy}
            data-testid="email-settings-test-send" className="rounded-full whitespace-nowrap">
            Send test
          </Button>
        </div>
      </div>
    </Card>
  );
}


function AdminVolunteers({ volunteers, reload }) {
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchBusy, setLaunchBusy] = useState(false);
  const [launchPreview, setLaunchPreview] = useState(null);
  const [launchMode, setLaunchMode] = useState("dry"); // dry | test | send
  const [testEmail, setTestEmail] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return volunteers;
    return volunteers.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        v.email.toLowerCase().includes(q) ||
        (v.phone || "").toLowerCase().includes(q),
    );
  }, [search, volunteers]);

  async function toggleWoofPack(v) {
    try {
      await api.patch(`/admin/volunteers/${v.id}/woof-pack?approved=${!v.woof_pack_approved}`);
      toast.success(`${v.name} · Woof Pack ${!v.woof_pack_approved ? "approved" : "removed"}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  }

  async function runLaunch(mode) {
    setLaunchBusy(true);
    try {
      const body = {
        dry_run: mode === "dry",
        only_email: mode === "test" ? testEmail.trim() : null,
      };
      const { data } = await api.post("/admin/send-launch-emails", body);
      setLaunchPreview(data);
      if (mode === "send") {
        toast.success(`Sent ${data.sent} launch emails · ${data.failed} failed`);
      } else if (mode === "test") {
        if (data.sent > 0) {
          toast.success(`Test email sent to ${testEmail}`);
        } else {
          toast.error(data.errors?.[0] || data.note || "Test not sent — check the email is a registered volunteer");
        }
      } else {
        toast.info(`${data.targets} volunteer(s) would receive an email`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLaunchBusy(false);
    }
  }

  async function confirmDelete() {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/volunteers/${confirmTarget.id}`);
      toast.success(
        `Removed ${data.deleted_user}` +
          (data.removed_signups ? ` · ${data.removed_signups} signup(s) cleared` : "") +
          (data.removed_dog_walks ? ` · ${data.removed_dog_walks} walk record(s)` : ""),
      );
      setConfirmTarget(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl font-bold">Volunteers</h1>
        <div className="flex gap-2 w-full sm:w-auto">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or phone…"
            className="rounded-full h-10 w-full sm:w-64"
            data-testid="vol-search"
          />
          <Button onClick={() => setReportOpen(true)} data-testid="vol-hours-report-btn"
            variant="outline"
            className="rounded-full whitespace-nowrap">
            <CalendarDays className="w-4 h-4 mr-1" strokeWidth={1.5} /> Hours report
          </Button>
          <Button onClick={() => setAddOpen(true)} data-testid="vol-add-btn"
            className="rounded-full bg-fern hover:bg-fern-dark text-white whitespace-nowrap">
            <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Add volunteer
          </Button>
          <Button onClick={() => { setLaunchOpen(true); setLaunchPreview(null); }} data-testid="launch-emails-btn"
            className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white whitespace-nowrap">
            <Mail className="w-4 h-4 mr-1" strokeWidth={1.5} /> Send launch emails
          </Button>
        </div>
      </div>
      <EmailSettingsCard />
      <Card className="tactile-card border-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="p-3 label-caps">Name</th>
              <th className="p-3 label-caps">Email</th>
              <th className="p-3 label-caps">Phone</th>
              <th className="p-3 label-caps">Active</th>
              <th className="p-3 label-caps">Completed</th>
              <th className="p-3 label-caps">Hours</th>
              <th className="p-3 label-caps">Woof Pack</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.id} className="border-t border-border" data-testid={`vol-row-${v.id}`}>
                <td className="p-3 font-medium">{v.name}</td>
                <td className="p-3">{v.email}</td>
                <td className="p-3">{v.phone || "—"}</td>
                <td className="p-3">{v.active_shifts}</td>
                <td className="p-3">{v.completed_shifts}</td>
                <td className="p-3"><Badge className="bg-fern text-white rounded-full">{v.total_hours}h</Badge>{v.historical_hours > 0 && <span className="ml-1 text-xs text-muted-foreground">({v.historical_hours}h imported)</span>}</td>
                <td className="p-3">
                  <button type="button" onClick={() => toggleWoofPack(v)} data-testid={`woofpack-toggle-${v.id}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${v.woof_pack_approved ? "bg-ink text-white border-ink" : "bg-white text-muted-foreground border-border hover:border-ink"}`}>
                    {v.woof_pack_approved ? "✓ Approved" : "Not approved"}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditTarget(v)}
                    data-testid={`vol-edit-${v.id}`} aria-label={`Edit ${v.name}`}
                    className="mr-1 text-fern hover:text-fern-dark">
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmTarget(v)}
                    data-testid={`vol-delete-${v.id}`} aria-label={`Delete ${v.name}`}>
                    <Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.5} />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="p-8 text-center text-muted-foreground">
                  {volunteers.length === 0 ? "No volunteers yet." : "No volunteers match your search."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={launchOpen} onOpenChange={setLaunchOpen}>
        <DialogContent data-testid="launch-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send launch emails</DialogTitle>
            <DialogDescription>
              Every volunteer receives a personal welcome email with a one-time link to set their password.
              Test on your own email first, then send to everyone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <LaunchEmailEditor />
            <div className="bg-accent/40 rounded-xl p-4 text-sm space-y-2">
              <p className="font-medium">Three-step launch, safest to slowest:</p>
              <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                <li><span className="font-medium text-ink">Dry run</span> — count only, sends nothing</li>
                <li><span className="font-medium text-ink">Test to one email</span> — sends to just one address so you can preview</li>
                <li><span className="font-medium text-ink">Send to all</span> — the real thing (one email per volunteer)</li>
              </ol>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setLaunchMode("dry"); runLaunch("dry"); }} disabled={launchBusy} className="rounded-full flex-1">
                Dry run
              </Button>
              <Button variant="outline" onClick={() => setLaunchMode("test")} disabled={launchBusy} className="rounded-full flex-1">
                Test to one email
              </Button>
            </div>
            {launchMode === "test" && (
              <div className="space-y-2">
                <Label>Send test to</Label>
                <div className="flex gap-2">
                  <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.org"
                    data-testid="launch-test-email" className="rounded-xl h-10" />
                  <Button onClick={() => runLaunch("test")} disabled={!testEmail || launchBusy}
                    data-testid="launch-test-send" className="rounded-full bg-fern hover:bg-fern-dark text-white">
                    Send test
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Must match an existing volunteer&apos;s email in the app.</p>
              </div>
            )}
            {launchPreview && (
              <div className="border border-border rounded-xl p-3 text-sm space-y-1">
                <p className="font-medium">
                  {launchPreview.dry_run ? "Dry run result" : "Send result"}
                </p>
                <p className="text-muted-foreground">
                  Targets: <span className="text-ink">{launchPreview.targets}</span>
                  {typeof launchPreview.sent === "number" && launchPreview.sent > 0 && <> · Sent: <span className="text-ink">{launchPreview.sent}</span></>}
                  {typeof launchPreview.failed === "number" && launchPreview.failed > 0 && <> · <span className="text-destructive">Failed: {launchPreview.failed}</span></>}
                </p>
                {launchPreview.sample_recipients && (
                  <details className="text-xs text-muted-foreground mt-1">
                    <summary className="cursor-pointer">First 5 recipients</summary>
                    <ul className="mt-1">
                      {launchPreview.sample_recipients.map((r) => <li key={r.email}>· {r.name} — {r.email}</li>)}
                    </ul>
                  </details>
                )}
                {launchPreview.errors?.length > 0 && (
                  <details className="text-xs text-destructive mt-1">
                    <summary className="cursor-pointer">{launchPreview.errors.length} error(s)</summary>
                    <ul className="mt-1">{launchPreview.errors.map((e, i) => <li key={`${e}-${i}`}>· {e}</li>)}</ul>
                  </details>
                )}
              </div>
            )}
            <div className="border-t border-border pt-3">
              <Button onClick={() => runLaunch("send")} disabled={launchBusy} data-testid="launch-send-all"
                className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
                {launchBusy ? "Sending…" : `Send to all ${volunteers.length} volunteers`}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Real send — one email per volunteer, each with their own reset link.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <DialogContent data-testid="vol-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete volunteer?</DialogTitle>
            <DialogDescription>
              This permanently removes <span className="font-medium text-ink">{confirmTarget?.name}</span> ({confirmTarget?.email})
              and clears their shift signups and dog-walk history.
            </DialogDescription>
          </DialogHeader>
          {confirmTarget && (
            <div className="bg-accent/40 rounded-xl p-3 text-sm space-y-1">
              <p>· <span className="font-medium">{confirmTarget.active_shifts}</span> active shift(s) — slots will free up</p>
              <p>· <span className="font-medium">{confirmTarget.completed_shifts}</span> completed shift(s) — history removed</p>
              <p>· <span className="font-medium">{confirmTarget.total_hours}h</span> of logged hours removed</p>
              <p className="text-xs text-muted-foreground pt-1">Tip: export a CSV backup of your current volunteer hours first if your platform supports it.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmTarget(null)} className="rounded-full">Cancel</Button>
            <Button onClick={confirmDelete} disabled={busy} data-testid="vol-delete-confirm"
              className="rounded-full bg-destructive hover:bg-destructive/90 text-white">
              {busy ? "Deleting…" : "Yes, delete volunteer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddVolunteerDialog open={addOpen} onOpenChange={setAddOpen} reload={reload} />
      <HoursReportDialog open={reportOpen} onOpenChange={setReportOpen} />
      <EditVolunteerDialog target={editTarget} onOpenChange={(o) => !o && setEditTarget(null)} reload={reload} />
    </div>
  );
}

function EditVolunteerDialog({ target, onOpenChange, reload }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", historical_hours: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setForm({
        name: target.name || "",
        email: target.email || "",
        phone: target.phone || "",
        historical_hours: target.historical_hours != null ? String(target.historical_hours) : "0",
      });
    }
  }, [target]);

  async function save() {
    if (!target) return;
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    const hh = form.historical_hours === "" ? null : parseFloat(form.historical_hours);
    if (hh !== null && (Number.isNaN(hh) || hh < 0)) { toast.error("Historical hours must be a non-negative number"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
      };
      if (hh !== null) payload.historical_hours = hh;
      await api.patch(`/admin/volunteers/${target.id}`, payload);
      toast.success(`Updated ${payload.name}`);
      onOpenChange(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save changes");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-volunteer-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Edit volunteer</DialogTitle>
          <DialogDescription>
            Update the volunteer&apos;s profile. Changing their email will move future logins to the new address.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl" data-testid="edit-vol-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-xl" data-testid="edit-vol-email" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="rounded-xl" data-testid="edit-vol-phone" placeholder="Optional" />
          </div>
          <div>
            <Label>Historical hours <span className="text-muted-foreground font-normal">(pre-app hours, e.g. imported from Galaxy)</span></Label>
            <Input type="number" step="0.25" min="0" value={form.historical_hours}
              onChange={(e) => setForm({ ...form, historical_hours: e.target.value })}
              className="rounded-xl" data-testid="edit-vol-historical" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Added on top of the hours logged in-app. Set to 0 to remove any imported balance.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-vol-save"
            className="rounded-full bg-fern hover:bg-fern-dark text-white">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddVolunteerDialog({ open, onOpenChange, reload }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", send_invite: true, initial_password: "" });
  const [busy, setBusy] = useState(false);

  function reset() {
    setForm({ name: "", email: "", phone: "", send_invite: true, initial_password: "" });
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    if (!form.send_invite && (!form.initial_password || form.initial_password.length < 6)) {
      toast.error("Provide a temporary password (min 6 chars) or enable the invite email");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        send_invite: form.send_invite,
      };
      if (!form.send_invite) payload.initial_password = form.initial_password;
      const { data } = await api.post("/admin/volunteers", payload);
      if (data.invite_sent) {
        toast.success(`${data.name} added and invite sent to ${data.email}`);
      } else if (form.send_invite && data.invite_error) {
        toast.warning(`${data.name} added, but invite email failed: ${data.invite_error}`);
      } else {
        toast.success(`${data.name} added — share the temporary password directly.`);
      }
      reset();
      onOpenChange(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add volunteer");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="add-volunteer-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Add a volunteer</DialogTitle>
          <DialogDescription>
            Create a volunteer manually — no Galaxy Digital or CSV needed. By default they&apos;ll receive an invite email with a link to set their password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Doe" className="rounded-xl" data-testid="add-vol-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="jane@example.com" className="rounded-xl" data-testid="add-vol-email" />
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="555-000-0000" className="rounded-xl" data-testid="add-vol-phone" />
          </div>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-border bg-accent/20 cursor-pointer">
            <Switch checked={form.send_invite}
              onCheckedChange={(v) => setForm({ ...form, send_invite: v })}
              data-testid="add-vol-invite" />
            <div className="flex-1">
              <p className="text-sm font-medium">Send an invite email</p>
              <p className="text-xs text-muted-foreground">Emails them a set-password link (link never expires until used).</p>
            </div>
          </label>
          {!form.send_invite && (
            <div>
              <Label>Temporary password</Label>
              <Input type="text" value={form.initial_password}
                onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
                placeholder="min 6 characters"
                className="rounded-xl" data-testid="add-vol-password" />
              <p className="text-[11px] text-muted-foreground mt-1">
                You&apos;ll need to share this with them directly. They can change it after logging in.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="add-vol-submit"
              className="rounded-full bg-fern hover:bg-fern-dark text-white">
              {busy ? "Adding…" : "Add volunteer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HoursReportDialog({ open, onOpenChange }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const monthAgoISO = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgoISO);
  const [to, setTo] = useState(todayISO);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rowSearch, setRowSearch] = useState("");

  async function run() {
    if (!from || !to) { toast.error("Pick both dates"); return; }
    setBusy(true);
    try {
      const { data: d } = await api.get(`/admin/hours/report?from=${from}&to=${to}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load report");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { if (open) run(); /* auto-load on open */ }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    const q = rowSearch.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q));
  }, [data, rowSearch]);

  function downloadCsv() {
    if (!data?.rows) return;
    const header = ["Name", "Email", "Total hours", "Shift hours", "Ad-hoc hours", "Shifts", "Ad-hoc entries"];
    const lines = [header.join(",")].concat(
      data.rows.map((r) => [
        `"${(r.name || "").replace(/"/g, '""')}"`,
        `"${(r.email || "").replace(/"/g, '""')}"`,
        r.total_hours, r.shift_hours, r.adhoc_hours, r.shift_count, r.adhoc_count,
      ].join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `volunteer-hours_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hours-report-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Volunteer hours report</DialogTitle>
          <DialogDescription>
            Total hours (completed shifts + ad-hoc logged hours) for any date range. Dates are inclusive.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="rounded-xl h-11" data-testid="hours-report-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="rounded-xl h-11" data-testid="hours-report-to" />
            </div>
            <Button onClick={run} disabled={busy} data-testid="hours-report-run"
              className="rounded-full bg-fern hover:bg-fern-dark text-white h-11">
              {busy ? "Loading…" : "Search"}
            </Button>
          </div>

          {data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="tactile-card p-3">
                  <p className="label-caps">Total hours</p>
                  <p className="font-heading text-2xl font-semibold" data-testid="hours-report-total">{data.total_hours}</p>
                </div>
                <div className="tactile-card p-3">
                  <p className="label-caps">Volunteers</p>
                  <p className="font-heading text-2xl font-semibold">{data.volunteer_count}</p>
                </div>
                <div className="tactile-card p-3">
                  <p className="label-caps">Shifts</p>
                  <p className="font-heading text-2xl font-semibold">{data.shift_count}</p>
                </div>
                <div className="tactile-card p-3">
                  <p className="label-caps">Ad-hoc logs</p>
                  <p className="font-heading text-2xl font-semibold">{data.adhoc_count}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center justify-between">
                <Input value={rowSearch} onChange={(e) => setRowSearch(e.target.value)}
                  placeholder="Filter by name or email…" className="rounded-full h-9 max-w-xs"
                  data-testid="hours-report-filter" />
                <Button onClick={downloadCsv} variant="outline" disabled={!data.rows?.length}
                  data-testid="hours-report-csv" className="rounded-full">
                  <FileDown className="w-4 h-4 mr-1" strokeWidth={1.5} /> Download CSV
                </Button>
              </div>
              <Card className="tactile-card border-0 overflow-hidden max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr className="text-left">
                      <th className="p-2.5 label-caps">Volunteer</th>
                      <th className="p-2.5 label-caps text-right">Hours</th>
                      <th className="p-2.5 label-caps text-right hidden sm:table-cell">Shifts</th>
                      <th className="p-2.5 label-caps text-right hidden sm:table-cell">Ad-hoc</th>
                    </tr>
                  </thead>
                  <tbody data-testid="hours-report-rows">
                    {filteredRows.length === 0 ? (
                      <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">
                        {data.rows.length === 0 ? "No hours logged in this range." : "No matches for that filter."}
                      </td></tr>
                    ) : filteredRows.map((r) => (
                      <tr key={r.user_id} className="border-t border-border" data-testid={`hours-row-${r.user_id}`}>
                        <td className="p-2.5">
                          <p className="font-medium">{r.name}</p>
                          <p className="text-xs text-muted-foreground">{r.email}</p>
                        </td>
                        <td className="p-2.5 text-right font-semibold">{r.total_hours}h</td>
                        <td className="p-2.5 text-right hidden sm:table-cell">{r.shift_hours}h <span className="text-xs text-muted-foreground">({r.shift_count})</span></td>
                        <td className="p-2.5 text-right hidden sm:table-cell">{r.adhoc_hours}h <span className="text-xs text-muted-foreground">({r.adhoc_count})</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="hours-report-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Integration platform picker ----------
const VOLUNTEER_PLATFORMS = [
  { id: "galaxy", name: "Galaxy Digital", live: true, blurb: "Live sync of your volunteer roster, hours, and Woof Pack groups." },
  { id: "volunteerhub", name: "VolunteerHub", live: false, blurb: "Shift sync + hour posting for VolunteerHub-managed programs." },
  { id: "better_impact", name: "Better Impact", live: false, blurb: "Roster and hour sync for Better Impact / MyImpactPage." },
  { id: "volgistics", name: "Volgistics", live: false, blurb: "Two-way roster and hour sync for Volgistics." },
];

const ANIMAL_PLATFORMS = [
  { id: "shelterluv", name: "ShelterLuv", live: true, blurb: "Native REST API pull of your active cats and dogs, kennel status, and photos — so volunteers know who to walk and cuddle." },
  { id: "petpoint", name: "PetPoint", live: false, blurb: "Pulls your currently-available animal roster from PetPoint so volunteers see the right dogs and cats every shift." },
  { id: "chameleon", name: "Chameleon", live: false, blurb: "Pulls your currently-available animal roster from Chameleon (CMS / 20 / 22) so volunteers see the right dogs and cats every shift." },
];

function IntegrationPicker({ category, options, activeId, onChange }) {
  return (
    <div className="tactile-card border-0 p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4">
      <div className="flex-1">
        <p className="label-caps mb-1">Active {category === "animal" ? "shelter management" : "volunteer management"} platform</p>
        <p className="text-sm text-muted-foreground">Switch platforms to configure a different integration. Only one can be active at a time.</p>
      </div>
      <Select value={activeId} onValueChange={onChange}>
        <SelectTrigger className="rounded-full w-full md:w-[260px]" data-testid={`${category}-platform-picker`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.id} value={o.id} data-testid={`${category}-platform-option-${o.id}`}>
              <span className="flex items-center gap-2">
                {o.name}
                {o.live
                  ? <Badge className="bg-fern/15 text-fern rounded-full text-[10px] px-1.5 py-0">Live</Badge>
                  : <Badge className="bg-muted text-muted-foreground rounded-full text-[10px] px-1.5 py-0">Beta</Badge>}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function StubIntegrationPanel({ platform, category }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await api.post("/integrations/interest", { platform: platform.id, category, note: note.trim() });
      toast.success(`We'll reach out about activating ${platform.name}.`);
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="tactile-card border-0 p-6" data-testid={`stub-panel-${platform.id}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-full bg-amber-100 text-amber-700">
          <RefreshCw className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold">{platform.name} is in private beta</h2>
          <p className="text-sm text-muted-foreground mt-1">{platform.blurb}</p>
        </div>
      </div>
      <div className="rounded-xl bg-sand/70 border border-border/60 p-4 text-sm mb-4">
        <p className="text-ink mb-2">Because {platform.name} requires a partner API agreement, we activate it on a per-shelter basis.
        Let us know a bit about your setup and we&apos;ll get you wired up (typically 1&ndash;3 business days).</p>
        <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
          <li>Your {platform.name} account tier and roughly how many active animals or volunteers you manage</li>
          <li>Which fields you need synced (roster, status, photos, hours, etc.)</li>
          <li>Your best point of contact for the API credentials</li>
        </ul>
      </div>
      {sent ? (
        <div className="rounded-xl bg-fern/10 border border-fern/30 p-4 text-sm text-fern-dark" data-testid={`stub-sent-${platform.id}`}>
          Request received. We&apos;ll email you shortly at the address on file.
        </div>
      ) : (
        <div className="space-y-2">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder={`Tell us about your ${platform.name} setup…`}
            data-testid={`stub-note-${platform.id}`}
            className="rounded-xl"
          />
          <Button
            onClick={submit}
            disabled={busy}
            data-testid={`stub-submit-${platform.id}`}
            className="rounded-full bg-fern hover:bg-fern-dark text-white">
            {busy ? "Sending…" : `Request activation for ${platform.name}`}
          </Button>
        </div>
      )}
    </Card>
  );
}

function AdminShelterSync({ reload }) {
  const [activeId, setActiveId] = useState("shelterluv");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/integrations/config");
        setActiveId(data.animal_platform || "shelterluv");
      } catch (err) {
        console.error("Failed to load integration config:", err);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  async function change(next) {
    setActiveId(next);
    try {
      await api.put("/integrations/config", { animal_platform: next });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save selection");
    }
  }

  const platform = ANIMAL_PLATFORMS.find((p) => p.id === activeId) || ANIMAL_PLATFORMS[0];

  return (
    <div className="space-y-4" data-testid="admin-shelter-sync">
      <div>
        <h1 className="font-heading text-3xl font-bold">Shelter management sync</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pull your currently-available animal roster from the shelter management system your organization already runs — so
          volunteers see the real dogs and cats in the building on the day of their shift.
        </p>
      </div>
      <IntegrationPicker
        category="animal"
        options={ANIMAL_PLATFORMS}
        activeId={activeId}
        onChange={change}
      />
      {loaded && (platform.live
        ? <AdminShelterLuv reload={reload} />
        : <StubIntegrationPanel platform={platform} category="animal" />)}
    </div>
  );
}

function AdminVolunteerSync({ reload }) {
  const [activeId, setActiveId] = useState("galaxy");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/integrations/config");
        setActiveId(data.volunteer_platform || "galaxy");
      } catch (err) {
        console.error("Failed to load integration config:", err);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  async function change(next) {
    setActiveId(next);
    try {
      await api.put("/integrations/config", { volunteer_platform: next });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save selection");
    }
  }

  const platform = VOLUNTEER_PLATFORMS.find((p) => p.id === activeId) || VOLUNTEER_PLATFORMS[0];

  return (
    <div className="space-y-4" data-testid="admin-volunteer-sync">
      <div>
        <h1 className="font-heading text-3xl font-bold">Volunteer management sync</h1>
        <p className="text-sm text-muted-foreground mt-1">Connect Shelter Focus to the volunteer platform where your roster and lifetime hours already live.</p>
      </div>
      <IntegrationPicker
        category="volunteer"
        options={VOLUNTEER_PLATFORMS}
        activeId={activeId}
        onChange={change}
      />
      {loaded && (platform.live
        ? <AdminGalaxy reload={reload} />
        : <StubIntegrationPanel platform={platform} category="volunteer" />)}
    </div>
  );
}


function AdminGalaxy({ reload }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Live-sync state
  const [liveStatus, setLiveStatus] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [liveResult, setLiveResult] = useState(null);

  const loadLiveStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/galaxy/live/status");
      setLiveStatus(data);
      if (data.last_result) setLiveResult(data.last_result);
    } catch (err) {
      console.error("Failed to load Galaxy live status:", err);
    }
  }, []);
  useEffect(() => { loadLiveStatus(); }, [loadLiveStatus]);

  async function runLiveSync() {
    setLiveBusy(true);
    try {
      const { data } = await api.post("/galaxy/live/sync");
      if (data.status === "already_running") {
        toast.info("A sync is already running.");
      } else {
        toast.success("Sync started — this can take a few minutes for large rosters.");
      }
      // Poll status every 3s until it stops running
      const startedAt = data.started_at;
      let done = false;
      for (let i = 0; i < 240 && !done; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: s } = await api.get("/galaxy/live/status");
          setLiveStatus(s);
          if (s.last_result) setLiveResult(s.last_result);
          if (!s.running && s.last_sync && s.last_sync > startedAt) {
            done = true;
            if (s.last_error) {
              toast.error(`Sync failed: ${s.last_error}`);
            } else if (s.last_result) {
              const r = s.last_result;
              toast.success(`Sync complete · ${r.volunteers_fetched} volunteers · ${r.total_historical_hours}h of history · ${r.created} new, ${r.updated} updated`);
            }
            break;
          }
        } catch (err) {
          console.error("Status poll failed:", err);
        }
      }
      await loadLiveStatus();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed to start");
    } finally {
      setLiveBusy(false);
    }
  }

  async function downloadExport() {
    try {
      const res = await api.get("/galaxy/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `galaxy_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error("Failed"); }
  }

  async function doImport() {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/galaxy/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data); toast.success(`Imported ${res.data.created} volunteers`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  const liveConfigured = !!liveStatus?.configured;

  return (
    <div className="space-y-6">
      <div>
        <p className="label-caps">Integration</p>
        <h1 className="font-heading text-3xl font-bold">Galaxy Digital sync</h1>
        <p className="text-sm text-muted-foreground mt-1">Live API sync — plus CSV fallback for offline import/export.</p>
      </div>

      {/* Live API sync panel */}
      <Card className="tactile-card border-0 p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${liveConfigured ? "bg-fern/10" : "bg-muted"}`}>
            <RefreshCw className={`w-5 h-5 ${liveConfigured ? "text-fern" : "text-muted-foreground"}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="font-medium">
              Live sync: {liveConfigured
                ? <span className="text-fern">Ready</span>
                : <span className="text-terracotta">Needs credentials</span>}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              API key: {liveStatus?.has_key ? "✓" : "—"} · Login: {liveStatus?.has_credentials ? "✓" : "—"}
            </p>
            {!liveConfigured && (
              <div className="mt-3 bg-accent/40 rounded-xl p-3 text-sm space-y-1">
                <p className="font-medium">Add these to <span className="font-mono">/app/backend/.env</span>:</p>
                <pre className="text-xs font-mono text-muted-foreground overflow-x-auto">GALAXY_API_KEY=&quot;your-key&quot;
GALAXY_EMAIL=&quot;your-galaxy-admin@example.org&quot;
GALAXY_PASSWORD=&quot;your-galaxy-admin-password&quot;</pre>
                <p className="text-xs text-muted-foreground mt-2">Then restart the backend and click Sync now.</p>
              </div>
            )}
          </div>
        </div>

        {liveStatus?.running && liveStatus?.progress && (
          <div className="border-t border-border pt-3">
            <p className="label-caps mb-1">In progress · {liveStatus.progress.phase?.replace(/_/g, " ")}</p>
            <p className="text-xs text-muted-foreground">
              Volunteers fetched: {liveStatus.progress.volunteers || 0} · Hours records: {liveStatus.progress.hours_records || 0} · Users with hours: {liveStatus.progress.hours_users || 0} · Written: {(liveStatus.progress.created || 0) + (liveStatus.progress.updated || 0)}
            </p>
          </div>
        )}

        {!liveStatus?.running && liveStatus?.last_sync && (
          <div className="border-t border-border pt-3">
            <p className="label-caps mb-1">Last sync</p>
            <p className="text-sm text-muted-foreground">{new Date(liveStatus.last_sync).toLocaleString()}</p>
            {liveStatus.last_error && <p className="text-xs text-destructive mt-1">Error: {liveStatus.last_error}</p>}
            {liveResult && !liveStatus.last_error && (
              <p className="text-xs text-muted-foreground mt-1">
                {liveResult.volunteers_fetched} volunteers · {liveResult.created} new · {liveResult.updated} updated · <span className="font-medium text-ink">{liveResult.total_historical_hours}h</span> of history imported ({liveResult.unique_volunteers_with_hours} volunteers)
              </p>
            )}
          </div>
        )}

        <Button onClick={runLiveSync} disabled={!liveConfigured || liveBusy} data-testid="galaxy-live-sync"
          className="rounded-full bg-fern hover:bg-fern-dark text-white">
          <RefreshCw className={`w-4 h-4 mr-1 ${liveBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
          {liveBusy ? "Syncing… (may take a minute)" : "Sync from Galaxy Digital"}
        </Button>
      </Card>

      {/* CSV fallback */}
      <div>
        <p className="label-caps mb-2">CSV fallback</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="tactile-card border-0 p-6">
            <FileDown className="w-6 h-6 text-fern mb-3" strokeWidth={1.5} />
          <h3 className="font-heading text-lg font-semibold mb-1">Export hours</h3>
          <p className="text-sm text-muted-foreground mb-4">Download a Galaxy Digital-compatible CSV of completed volunteer hours.</p>
          <Button onClick={downloadExport} data-testid="galaxy-export" className="rounded-full bg-fern hover:bg-fern-dark text-white">
            Download CSV
          </Button>
        </Card>

        <Card className="tactile-card border-0 p-6">
          <Upload className="w-6 h-6 text-terracotta mb-3" strokeWidth={1.5} />
          <h3 className="font-heading text-lg font-semibold mb-1">Import volunteers</h3>
          <p className="text-sm text-muted-foreground mb-4">CSV with columns: <span className="font-mono">email, name, phone</span>. New volunteers get default password <span className="font-mono">satchels2026</span>.</p>
          <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="galaxy-file" className="rounded-xl mb-3" />
          <Button onClick={doImport} disabled={!file || busy} data-testid="galaxy-import" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
            {busy ? "Importing…" : "Import"}
          </Button>
          {result && (
            <p className="text-sm text-muted-foreground mt-3">Created {result.created}, skipped {result.skipped}.</p>
          )}
        </Card>
        </div>
      </div>
    </div>
  );
}

function AdminShelterLuv({ reload }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/shelterluv/status");
      setStatus(data);
      if (data.last_result) setLastResult(data.last_result);
    } catch (err) {
      console.error("Failed to load ShelterLuv status:", err);
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function runSync() {
    if (!status?.configured) {
      toast.error("Configure SHELTERLUV_API_KEY in backend .env first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/shelterluv/sync");
      setLastResult(data);
      toast.success(`Synced ${data.fetched} animals · ${data.created} new, ${data.updated} updated, ${data.skipped} skipped`);
      await loadStatus();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  }

  const configured = !!status?.configured;

  return (
    <div className="space-y-6">
      <div>
        <p className="label-caps">Integration</p>
        <h1 className="font-heading text-3xl font-bold">ShelterLuv sync</h1>
        <p className="text-sm text-muted-foreground mt-1">Pull dogs and cats from your ShelterLuv account into the app. Idempotent — running twice will not create duplicates.</p>
      </div>

      <Card className="tactile-card border-0 p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${configured ? "bg-fern/10" : "bg-muted"}`}>
            <KeyRound className={`w-5 h-5 ${configured ? "text-fern" : "text-muted-foreground"}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="font-medium">
              API key: {configured
                ? <span className="text-fern">Configured</span>
                : <span className="text-terracotta">Not configured</span>}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Base URL: <span className="font-mono">{status?.base_url || "—"}</span>
            </p>
            {!configured && (
              <div className="mt-3 bg-accent/40 rounded-xl p-3 text-sm space-y-1">
                <p className="font-medium">How to set it up:</p>
                <ol className="list-decimal list-inside text-muted-foreground space-y-0.5">
                  <li>Log into ShelterLuv → <span className="font-mono">Configuration</span> → scroll to <span className="font-mono">API</span></li>
                  <li>Click <span className="font-mono">Generate New Key</span>, name it &quot;Volunteer App&quot;</li>
                  <li>Paste it into <span className="font-mono">/app/backend/.env</span> as <span className="font-mono">SHELTERLUV_API_KEY=&quot;...&quot;</span></li>
                  <li>Restart the backend (<span className="font-mono">sudo supervisorctl restart backend</span>)</li>
                </ol>
              </div>
            )}
          </div>
        </div>

        {status?.last_sync && (
          <div className="border-t border-border pt-3">
            <p className="label-caps mb-1">Last sync</p>
            <p className="text-sm text-muted-foreground">{new Date(status.last_sync).toLocaleString()}</p>
            {lastResult && (
              <p className="text-xs text-muted-foreground mt-1">
                Fetched {lastResult.fetched} · Created {lastResult.created} · Updated {lastResult.updated} · Skipped {lastResult.skipped}
              </p>
            )}
          </div>
        )}

        <Button onClick={runSync} disabled={!configured || busy} data-testid="shelterluv-sync"
          className="rounded-full bg-fern hover:bg-fern-dark text-white">
          <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} strokeWidth={1.5} />
          {busy ? "Syncing…" : "Sync now"}
        </Button>
      </Card>

      {lastResult?.errors?.length > 0 && (
        <Card className="tactile-card border-0 p-5">
          <p className="label-caps mb-2">Recent errors</p>
          <ul className="text-xs text-destructive space-y-1">
            {lastResult.errors.map((e, i) => <li key={`${e}-${i}`}>· {e}</li>)}
          </ul>
        </Card>
      )}
    </div>
  );
}

